import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { findCourseBySlug, getLessonUrl, getLessonNumber } from '../utils/urlUtils';
import { useProgress } from '../contexts/ProgressContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import AnalyticsService from '../services/analyticsService';
import Header from './Header';
import Breadcrumb from './Breadcrumb';
import { BodyMedium, TitleLarge } from './Typography';
import { renderMarkdownToHTML } from '../utils/markdownRenderer';
import ActionButton from './ActionButton';
import hexagonBorderImage from '../images/hexagon_white_border.png';
import hexagonLightGreyImage from '../images/hexagon_lightgrey.png';
import hexagonWhiteImage from '../images/hexagon_white.png';
import lockIcon from '../images/lock_icon.png';
import clockIcon from '../images/Hours to Complete Icon.png';
import skillIcon from '../images/Skill Level Icon.png';
import lessonsIcon from '../images/Lessons Icon.png';

// Navy used on light hexagon cards (matches CourseCard / PromptCard)
const HEXAGON_TEXT = '#091E42';
const HEXAGON_TEXT_MUTED = '#0A4872';

const CourseOverview = ({ onBack, onNavigateToHome, onNavigateToProfile, onNavigateToPrompts, onNavigateToWritersRoom, onLogOut, onStartLesson, onModeSwitch, showAdminMode = false }) => {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firstLessonId, setFirstLessonId] = useState(null);
  const [allCourseLessons, setAllCourseLessons] = useState([]);
  const [allCourses, setAllCourses] = useState([]);

  const { findNextIncompleteLesson, getLevelProgress, getCourseStatus, canAccessLesson, isLessonCompleted, getUserCourse } = useProgress();
  const { user, currentSession, userOrganizations } = useAuth();
  const { themeColors } = useTheme();

  const findFirstLesson = useCallback(async (courseId) => {
    try {
      const lessonsRef = collection(db, 'lessons');
      const firstLessonQuery = query(
        lessonsRef,
        where('course_id', '==', courseId),
        where('level', '==', 1),
        where('lesson_number', '==', 1)
      );
      
      const firstLessonSnapshot = await getDocs(firstLessonQuery);
      
      if (!firstLessonSnapshot.empty) {
        const firstLesson = firstLessonSnapshot.docs[0];
        setFirstLessonId(firstLesson.id);
      } else {
        // SIMPLE: Handle missing first lesson gracefully
        setFirstLessonId(null);
      }
    } catch (error) {
      // EFFICIENT: Silent error handling
      setFirstLessonId(null);
    }
  }, []);

  const fetchLevelLessons = useCallback(async (courseId) => {
    try {
      const lessonsRef = collection(db, 'lessons');
      
      // Fetch all lessons for the course in one query
      const allLevelsQuery = query(
        lessonsRef,
        where('course_id', '==', courseId)
      );
      
      const allLevelsSnapshot = await getDocs(allLevelsQuery);
      const allLessons = allLevelsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Store all course lessons for smart routing
      setAllCourseLessons(allLessons);
      
      // Group lessons by level
      const lessonsByLevel = {};
      for (let level = 1; level <= 6; level++) {
        const levelLessons = allLessons.filter(lesson => lesson.level === level);
        levelLessons.sort((a, b) => a.lesson_number - b.lesson_number);
        lessonsByLevel[level] = levelLessons;
      }
      
      // setLevelLessons(lessonsByLevel); // This line is removed
      
      // Return the lessons for immediate use
      return allLessons;
    } catch (error) {
      // EFFICIENT: Return empty array to maintain functionality
      return [];
    }
  }, []);

  const fetchAllCourses = useCallback(async () => {
    try {
      const coursesRef = collection(db, 'courses');
      const coursesSnapshot = await getDocs(coursesRef);
      const courses = coursesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllCourses(courses);
      return courses;
    } catch (error) {
      setAllCourses([]);
      return [];
    }
  }, []);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        
        // First fetch all courses to find the one with matching slug
        const courses = await fetchAllCourses();
        const matchedCourse = findCourseBySlug(courses, courseSlug);
        
        if (matchedCourse) {
          setCourse(matchedCourse);
          
          // Update lastAccessed timestamp for this course (minimal overhead - ties into existing read)
          if (user) {
            getUserCourse(matchedCourse.id).catch(() => {
              // Silently fail - don't block UI if this fails
            });
          }
          
          // Track course overview access analytics (every time page loads)
          if (user && currentSession && userOrganizations) {
            await AnalyticsService.trackAccess(
              'course',
              matchedCourse.id,
              currentSession,
              user.uid,
              userOrganizations,
              {
                courseTitle: matchedCourse.title || 'Untitled Course',
                courseDescription: matchedCourse.description || 'No description available',
                action: 'course_overview_accessed'
              }
            );
          }
          
          // Find the first lesson of this course
          await findFirstLesson(matchedCourse.id);
          
          // Fetch lessons for all levels and wait for it to complete
          const lessons = await fetchLevelLessons(matchedCourse.id);
          // EFFICIENT: Lessons loaded successfully
        } else {
          // SIMPLE: Handle missing course gracefully - redirect to courses
          setCourse(null);
          navigate('/courses');
        }
        setLoading(false);
      } catch (error) {
        // EFFICIENT: Silent error handling
        setCourse(null);
        setLoading(false);
      }
    };

    if (courseSlug) {
      fetchCourse();
    }
  }, [courseSlug, fetchAllCourses, fetchLevelLessons, findFirstLesson, user, currentSession, userOrganizations, navigate]);

  const handleGetStarted = () => {
    if (!course) return;
    
    // Find the next incomplete lesson using smart routing
    // If all lessons are completed, this will return the first lesson for a fresh start
    const nextLesson = findNextIncompleteLesson(course.id, allCourseLessons);
    
    if (nextLesson) {
      // Navigate to the specific lesson within the level
      const levelLessons = allCourseLessons.filter(l => l.level === nextLesson.level);
      const lessonNumber = getLessonNumber(nextLesson, levelLessons);
      navigate(getLessonUrl(course, nextLesson.level, lessonNumber));
    } else {
      // Fallback: if no lesson found, try to find the first lesson of the course
      if (allCourseLessons.length > 0) {
        const firstLesson = allCourseLessons
          .sort((a, b) => {
            if (a.level !== b.level) return a.level - b.level;
            return a.lesson_number - b.lesson_number;
          })[0];
        
        if (firstLesson) {
          // Find the correct lesson number within the level
          const levelLessons = allCourseLessons.filter(l => l.level === firstLesson.level);
          const lessonNumber = getLessonNumber(firstLesson, levelLessons);
          navigate(getLessonUrl(course, firstLesson.level, lessonNumber));
          return;
        }
      }
      
      // SIMPLE: Silent handling - button will show loading state
    }
  };

  const isLevelClickable = (level) => {
    // Get all lessons for this level
    const levelLessons = allCourseLessons.filter(lesson => lesson.level === level.level);
    if (levelLessons.length === 0) return false;
    
    // Sort lessons by lesson_number to get the first lesson of the level
    const sortedLevelLessons = levelLessons.sort((a, b) => a.lesson_number - b.lesson_number);
    const firstLessonOfLevel = sortedLevelLessons[0];
    
    // A level is clickable if its first lesson can be accessed (sequential completion)
    return canAccessLesson(firstLessonOfLevel.id, allCourseLessons);
  };

  const handleLevelClick = async (level) => {
    if (!course) return;
    

    
    // Get all lessons for this specific level
    const levelLessons = allCourseLessons.filter(lesson => lesson.level === level.level);
    if (levelLessons.length === 0) return;
    
    // Sort lessons by lesson_number
    const sortedLevelLessons = levelLessons.sort((a, b) => a.lesson_number - b.lesson_number);
    
    // Use the same smart routing logic as the "Get Started" button, but limited to this level
    const nextLesson = findNextIncompleteLesson(course.id, sortedLevelLessons);
    
    if (nextLesson) {
      // Found an incomplete lesson in this level - navigate to the specific lesson
      const lessonNumber = getLessonNumber(nextLesson, sortedLevelLessons);
      navigate(getLessonUrl(course, nextLesson.level, lessonNumber));
    } else {
      // All lessons in this level are completed, go to the first lesson for review
      if (sortedLevelLessons.length > 0) {
        const firstLesson = sortedLevelLessons[0];
        const lessonNumber = getLessonNumber(firstLesson, sortedLevelLessons);
        navigate(getLessonUrl(course, firstLesson.level, lessonNumber));
      }
    }
  };

  const getLevelHexagonImage = (level) => {
    // Get level progress to determine the appropriate hexagon image
    const levelProgress = getLevelProgress(level.level, allCourseLessons);
    
    if (!levelProgress) return hexagonLightGreyImage;
    
    if (levelProgress.isCompleted) {
      // Completed level - use light grey hexagon
      return hexagonLightGreyImage;
    } else if (levelProgress.isActive) {
      // Active level - use white hexagon
      return hexagonWhiteImage;
    } else {
      // Not started level - use light grey hexagon
      return hexagonLightGreyImage;
    }
  };

  const getButtonText = () => {
    const courseStatus = getCourseStatus(course?.id, allCourseLessons);
    
    switch (courseStatus) {
      case 'completed':
        return 'Review Course';
      case 'in_progress':
        return 'Jump Back In';
      default:
        return 'Get Started';
    }
  };

  if (loading) {
    return (
      /* role=status announces loading to screen readers (WCAG 4.1.3 Status Messages) */
      <div role="status" aria-live="polite" className="flex items-center justify-center py-12">
        <div className="text-center">
          <div aria-hidden="true" className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: themeColors.accent }}></div>
          <div className="mt-4">
            <BodyMedium style={{ color: themeColors.textSecondary }}>Loading course...</BodyMedium>
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <TitleLarge style={{ color: themeColors.textSecondary }} className="mb-4">Course not found</TitleLarge>
          <button 
            onClick={onBack}
            className="px-6 py-3 rounded-full transition-colors text-white"
            style={{ backgroundColor: themeColors.accent }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.accentHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = themeColors.accent}
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  const handleNavigateToCourses = () => {
    onBack();
  };

  const courseLevels = [
    { level: 1, title: 'Exposition' },
    { level: 2, title: 'Rising Action' },
    { level: 3, title: 'Climax' },
    { level: 4, title: 'Falling Action' },
    { level: 5, title: 'Resolution' },
    { level: 6, title: 'Appendix' }
  ];

  return (
    <div>
      {/* Header */}
      <Header 
        title={course.course_name || course.title}
        onNavigateToHome={onNavigateToHome}
        onNavigateToCourses={handleNavigateToCourses}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToPrompts={onNavigateToPrompts}
        onNavigateToWritersRoom={onNavigateToWritersRoom}
        onLogOut={onLogOut}
        onModeSwitch={onModeSwitch}
        showAdminMode={showAdminMode}
      />

      {/* Main Content */}
      <div className="w-full px-4 sm:px-8 md:px-[90px] pt-4 md:pt-6 pb-8 md:pb-12 min-h-[calc(100vh-100px)] flex flex-col justify-center">
        {/* Theme-aware styles for markdown content */}
        <style>{`
          .course-description p,
          .course-description h1,
          .course-description h2,
          .course-description h3,
          .course-description h4,
          .course-description li,
          .course-description strong,
          .course-description em,
          .course-description u,
          .course-description blockquote {
            color: ${themeColors.textPrimary} !important;
          }
        `}</style>
        {/* Breadcrumbs */}
        <Breadcrumb 
          items={[
            { 
              label: 'Home', 
              onClick: onNavigateToHome 
            },
            { 
              label: 'Courses', 
              onClick: handleNavigateToCourses 
            },
            { 
              label: course.course_name || course.title 
            }
          ]} 
        />
        {/* Course Information Card */}
        <div>
          {/* Mobile Layout: Hexagon first, then content */}
          <div className="lg:hidden">
            {/* Mobile: Stats Hexagon on top */}
            <div className="flex justify-center mb-8">
              <div className="relative w-[240px] h-[240px]">
                {/* Hexagon Background */}
                <div className="absolute inset-0">
                  <img 
                    src={hexagonBorderImage} 
                    alt=""
                    role="presentation"
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {/* Stats Content */}
                <div className="absolute inset-0 flex flex-col justify-center items-center p-8">
                  <div className="space-y-3 w-full px-8">
                    {/* Time to Complete */}
                    <div className="flex items-start space-x-3">
                      <img 
                        src={clockIcon} 
                        alt="" role="presentation" 
                        className="w-6 h-6 mt-0.5 flex-shrink-0"
                        style={{ filter: 'brightness(0)' }}
                      />
                      <div className="flex flex-col">
                        <span className="font-poppins text-[12px] font-light mb-1" style={{ color: HEXAGON_TEXT_MUTED }}>
                          TIME TO COMPLETE
                        </span>
                        <span className="font-poppins text-[14px] font-bold whitespace-nowrap" style={{ color: HEXAGON_TEXT }}>
                          {course.hours_to_complete || 'Unknown'} Hours
                        </span>
                      </div>
                    </div>

                    {/* Skill Level */}
                    <div className="flex items-start space-x-3">
                      <img 
                        src={skillIcon} 
                        alt="" role="presentation" 
                        className="w-6 h-6 mt-0.5 flex-shrink-0"
                        style={{ filter: 'brightness(0)' }}
                      />
                      <div className="flex flex-col">
                        <span className="font-poppins text-[14px] font-bold" style={{ color: HEXAGON_TEXT }}>
                          {course.difficulty || 'Beginner'}
                        </span>
                      </div>
                    </div>

                    {/* Lessons */}
                    <div className="flex items-start space-x-3">
                      <img 
                        src={lessonsIcon} 
                        alt="" role="presentation" 
                        className="w-6 h-6 mt-0.5 flex-shrink-0"
                        style={{ filter: 'brightness(0)' }}
                      />
                      <div className="flex flex-col">
                        <span className="font-poppins text-[12px] font-light mb-1" style={{ color: HEXAGON_TEXT_MUTED }}>
                          LESSONS
                        </span>
                        <span className="font-poppins text-[14px] font-bold" style={{ color: HEXAGON_TEXT }}>
                          {course.lessons_count || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile: Course Details below hexagon */}
            <div className="text-center">
              <h1 className="font-poppins text-[22px] font-bold mb-4" style={{ color: themeColors.textPrimary }}>
                {course.course_headline || course.course_name || course.title}
              </h1>
              <div 
                className="font-inter text-[16px] leading-relaxed mb-6 course-description"
                style={{ color: themeColors.textPrimary }}
                dangerouslySetInnerHTML={renderMarkdownToHTML(course.course_description || course.description)}
              />
              
              {/* Start Course Button */}
              <ActionButton
                onClick={handleGetStarted}
                disabled={!firstLessonId || allCourseLessons.length === 0}
                showArrow={true}
                size="lg"
                className="mx-auto"
              >
                {firstLessonId && allCourseLessons.length > 0 ? getButtonText() : 'Loading...'}
              </ActionButton>
            </div>
          </div>

          {/* Desktop Layout: Original grid layout */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-8">
            {/* Left Column - Course Details */}
            <div className="lg:col-span-2 flex items-center">
              <div className="w-full">
                <h1 className="font-poppins text-[22px] font-bold mb-4" style={{ color: themeColors.textPrimary }}>
                  {course.course_headline || course.course_name || course.title}
                </h1>
                <div 
                  className="font-inter text-[16px] leading-relaxed mb-6 course-description"
                  style={{ color: themeColors.textPrimary }}
                  dangerouslySetInnerHTML={renderMarkdownToHTML(course.course_description || course.description)}
                />
                
                {/* Start Course Button */}
                <ActionButton
                  onClick={handleGetStarted}
                  disabled={!firstLessonId || allCourseLessons.length === 0}
                  showArrow={true}
                  size="lg"
                >
                  {firstLessonId && allCourseLessons.length > 0 ? getButtonText() : 'Loading...'}
                </ActionButton>
              </div>
            </div>

            {/* Right Column - Course Stats Hexagon */}
            <div className="flex justify-center">
              <div className="relative w-[260px] h-[260px]">
                {/* Hexagon Background */}
                <div className="absolute inset-0">
                  <img 
                    src={hexagonBorderImage} 
                    alt=""
                    role="presentation"
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {/* Stats Content */}
                <div className="absolute inset-0 flex flex-col justify-center items-center p-10">
                  <div className="space-y-3 w-full px-10">
                    {/* Time to Complete */}
                    <div className="flex items-start space-x-3">
                      <img 
                        src={clockIcon} 
                        alt="" role="presentation" 
                        className="w-6 h-6 mt-0.5 flex-shrink-0"
                        style={{ filter: 'brightness(0)' }}
                      />
                      <div className="flex flex-col">
                        <span className="font-poppins text-[12px] font-light mb-1" style={{ color: HEXAGON_TEXT_MUTED }}>
                          TIME TO COMPLETE
                        </span>
                        <span className="font-poppins text-[14px] font-bold whitespace-nowrap" style={{ color: HEXAGON_TEXT }}>
                          {course.hours_to_complete || 'Unknown'} Hours
                        </span>
                      </div>
                    </div>

                    {/* Skill Level */}
                    <div className="flex items-start space-x-3">
                      <img 
                        src={skillIcon} 
                        alt="" role="presentation" 
                        className="w-6 h-6 mt-0.5 flex-shrink-0"
                        style={{ filter: 'brightness(0)' }}
                      />
                      <div className="flex flex-col">
                        <span className="font-poppins text-[14px] font-bold" style={{ color: HEXAGON_TEXT }}>
                          {course.difficulty || 'Beginner'}
                        </span>
                      </div>
                    </div>

                    {/* Lessons */}
                    <div className="flex items-start space-x-3">
                      <img 
                        src={lessonsIcon} 
                        alt="" role="presentation" 
                        className="w-6 h-6 mt-0.5 flex-shrink-0"
                        style={{ filter: 'brightness(0)' }}
                      />
                      <div className="flex flex-col">
                        <span className="font-poppins text-[12px] font-light mb-1" style={{ color: HEXAGON_TEXT_MUTED }}>
                          LESSONS
                        </span>
                        <span className="font-poppins text-[14px] font-bold" style={{ color: HEXAGON_TEXT }}>
                          {course.lessons_count || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Course Levels Section */}
        <div className="mt-12">
          {/* Start Course button is now inside the left column above */}
          
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 w-full">
            {courseLevels.map((level) => {
              const levelProgress = getLevelProgress(level.level, allCourseLessons);
              const isClickable = isLevelClickable(level);
              const hexagonImage = getLevelHexagonImage(level);
              
              return (
                <div key={level.level} className="relative">
                  {/* Level Hexagon */}
                  <button
                    type="button"
                    aria-disabled={!isClickable}
                    tabIndex={isClickable ? 0 : -1}
                    aria-label={`Level ${level.level}: ${level.title}${!isClickable ? ' (locked)' : ''}`}
                    className={`relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 xl:w-40 xl:h-40 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-sm ${
                      isClickable 
                        ? 'cursor-pointer hover:scale-105' 
                        : 'cursor-not-allowed opacity-60'
                    }`}
                    onClick={() => isClickable && handleLevelClick(level)}
                  >
                    {/* Hexagon image is decorative; button label conveys level info */}
                    <img 
                      src={hexagonImage}
                      alt=""
                      role="presentation"
                      className="w-full h-full object-contain"
                    />
                    
                    {/* Level Content — hidden from AT since button label contains all info */}
                    <div aria-hidden="true" className="absolute inset-0 flex flex-col items-center justify-center text-center" style={{ color: HEXAGON_TEXT }}>
                      <div className="font-poppins text-[10px] sm:text-xs md:text-xs lg:text-sm font-light" style={{ color: HEXAGON_TEXT_MUTED }}>
                        LEVEL {level.level}
                      </div>
                      <div className="font-poppins text-xs sm:text-sm md:text-base lg:text-lg font-medium mt-1 sm:mt-2" style={{ color: HEXAGON_TEXT }}>
                        {level.title}
                      </div>
                      
                      {/* Progress Display */}
                      {levelProgress && (
                        <div className="font-poppins text-xs sm:text-sm font-medium mt-1 sm:mt-2" style={{ color: HEXAGON_TEXT }}>
                          {levelProgress.isCompleted ? (
                            "Revisit Level"
                          ) : levelProgress.isActive ? (
                            `${levelProgress.current}/${levelProgress.total}`
                          ) : (
                            // Not started level - show lock icon
                            <div className="flex items-center justify-center">
                              <img 
                                src={lockIcon} 
                                alt=""
                                role="presentation"
                                className="w-4 h-4 mr-2"
                                style={{ filter: 'brightness(0)' }}
                              />
                              <span>Locked</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseOverview;
